import { useState, useEffect } from "react";
import {
  X, Dumbbell, Trophy, Calendar, Flame, Zap,
  BookOpen, Clock, TrendingUp, User, ChevronUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

function StatKaart({ icon, label, value, sub }) {
  return (
    <div className="flex flex-col gap-1 p-4 rounded-2xl border border-white/8 bg-white/3">
      <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-white font-black text-2xl leading-none">{value}</p>
      {sub && <p className="text-zinc-600 text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

function PrRij({ pr, rank }) {
  const medailles = ["🥇", "🥈", "🥉"];
  const badge = rank < 3 ? medailles[rank] : null;
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
      <div className="flex items-center gap-3">
        {badge
          ? <span className="text-base w-6 text-center">{badge}</span>
          : <span className="text-zinc-700 text-xs font-bold w-6 text-center">#{rank + 1}</span>
        }
        <div>
          <p className="text-white text-sm font-semibold">{pr.naam}</p>
          {pr.datum && <p className="text-zinc-600 text-xs">{pr.datum}</p>}
        </div>
      </div>
      <div className="text-right">
        <p className="text-white font-black text-sm">{pr.beste_kg} kg</p>
        <p className="text-zinc-500 text-xs">1RM ~{pr.beste_1rm} kg</p>
      </div>
    </div>
  );
}

export default function SporterProfielModal({ sporter, token, onClose, onStartTraining }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sporter?.id) return;
    setLoading(true);
    fetch(`${API}/api/admin/sporter/${sporter.id}/profiel`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError("Profiel laden mislukt"); setLoading(false); });
  }, [sporter?.id, token]);

  const initials = (sporter?.naam || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const colors = ["#C9A84C", "#4C9A84", "#844CC9", "#C94C4C", "#4C84C9"];
  const kleur = colors[(sporter?.naam || "").charCodeAt(0) % colors.length];

  const schemaTypeBadge = (type) => {
    if (type === "bedrijf") return <span className="text-[10px] px-1.5 py-0.5 rounded-md font-bold bg-blue-500/10 text-blue-400">bedrijf</span>;
    if (type === "gekocht") return <span className="text-[10px] px-1.5 py-0.5 rounded-md font-bold" style={{ background: `${GOLD}15`, color: GOLD }}>gekocht</span>;
    return <span className="text-[10px] px-1.5 py-0.5 rounded-md font-bold bg-purple-500/10 text-purple-400">custom</span>;
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="sporter-profiel-modal"
        className="w-full sm:w-[660px] max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 flex flex-col"
        style={{ background: "#0d0d0d" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/8"
          style={{ background: "#0d0d0d" }}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black text-black shrink-0"
              style={{ background: kleur }}>
              {initials}
            </div>
            <div>
              <h2 className="text-white font-black text-lg leading-tight">{sporter?.naam}</h2>
              <p className="text-zinc-500 text-sm">{sporter?.email}</p>
              {data?.sporter?.role && (
                <span className="text-[11px] px-2 py-0.5 rounded-full font-bold mt-1 inline-block"
                  style={{ background: `${GOLD}15`, color: GOLD }}>
                  {data.sporter.role === "sporter" ? "Bedrijfssporter" : "B2C Klant"}
                </span>
              )}
            </div>
          </div>
          <button
            data-testid="sporter-profiel-sluiten"
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            <X size={16} className="text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
            </div>
          )}

          {error && (
            <p className="text-red-400 text-sm text-center py-8">{error}</p>
          )}

          {!loading && !error && data && (
            <>
              {/* Stats */}
              <div>
                <h3 className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-3">Statistieken</h3>
                <div className="grid grid-cols-3 gap-3">
                  <StatKaart
                    icon={<Dumbbell size={12} />}
                    label="Trainingen"
                    value={data.stats.totaal_sessies}
                    sub="totaal"
                  />
                  <StatKaart
                    icon={<Flame size={12} />}
                    label="Streak"
                    value={`${data.stats.streak_dagen}d`}
                    sub={`record: ${data.stats.record_streak}d`}
                  />
                  <StatKaart
                    icon={<Calendar size={12} />}
                    label="Dagen"
                    value={data.stats.trainingsdagen}
                    sub="met training"
                  />
                </div>
              </div>

              {/* PRs */}
              {data.prs.length > 0 && (
                <div>
                  <h3 className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Trophy size={12} style={{ color: GOLD }} />
                    Persoonlijke Records
                  </h3>
                  <div className="rounded-2xl border border-white/8 bg-white/2 px-4">
                    {data.prs.map((pr, i) => (
                      <PrRij key={pr.naam} pr={pr} rank={i} />
                    ))}
                  </div>
                </div>
              )}

              {data.prs.length === 0 && (
                <div className="text-center py-6 rounded-2xl border border-white/5 bg-white/2">
                  <Trophy size={28} className="text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-600 text-sm">Nog geen trainingen gelogd</p>
                </div>
              )}

              {/* Schema's */}
              {data.schemas.length > 0 && (
                <div>
                  <h3 className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                    <BookOpen size={12} />
                    Schema's
                  </h3>
                  <div className="space-y-2">
                    {data.schemas.map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl border border-white/8 bg-white/2">
                        <p className="text-white text-sm font-semibold">{s.naam}</p>
                        <div className="flex items-center gap-2">
                          {s.doel && <span className="text-zinc-600 text-xs">{s.doel}</span>}
                          {schemaTypeBadge(s.type)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recente trainingen */}
              {data.recente_trainingen.length > 0 && (
                <div>
                  <h3 className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Clock size={12} />
                    Recente Trainingen
                  </h3>
                  <div className="space-y-2">
                    {data.recente_trainingen.map((t, i) => (
                      <div key={i} className="px-4 py-3 rounded-xl border border-white/8 bg-white/2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm font-semibold">{t.naam}</p>
                            <p className="text-zinc-600 text-xs">{t.datum}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-zinc-400 text-xs">{t.oefeningen} oef · {t.sets} sets</p>
                          </div>
                        </div>
                        {t.opmerkingen?.length > 0 && (
                          <div className="mt-2 space-y-1 border-t border-white/5 pt-2" data-testid={`training-opmerkingen-${i}`}>
                            {t.opmerkingen.map((o, oi) => (
                              <p key={oi} className="text-xs text-zinc-400 italic">
                                <span className="text-[#C9A84C] not-italic font-semibold">{o.oefening}:</span> {o.notitie}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Aangemeld info */}
              {data.sporter.aangemeld_sinds && (
                <p className="text-zinc-700 text-xs text-center pb-2">
                  Aangemeld: {data.sporter.aangemeld_sinds?.slice(0, 10) || "onbekend"}
                </p>
              )}
            </>
          )}
        </div>

        {/* Sticky footer: start training */}
        <div className="sticky bottom-0 px-6 py-4 border-t border-white/8 flex gap-3"
          style={{ background: "#0d0d0d" }}>
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-bold hover:bg-white/5 transition-colors"
          >
            Sluiten
          </button>
          <button
            data-testid="sporter-profiel-start-training"
            onClick={() => { onClose(); onStartTraining(sporter); }}
            className="flex-1 py-3 rounded-xl font-black text-sm text-black flex items-center justify-center gap-2 transition-all active:scale-95"
            style={{ background: GOLD }}
          >
            <Dumbbell size={14} />
            Training starten
          </button>
        </div>
      </div>
    </div>
  );
}
